/**
 * CU POP Bus fleet simulator — the "simulation → production" half of the
 * shuttle pipeline (pattern borrowed from Phuket Smart Bus: schedules +
 * polylines today, a GPS feed swapped in via one import later).
 *
 * Pure and stateless: `simulateFleet(lines, now)` derives every bus position
 * from the wall clock alone, so the API and the browser compute identical
 * positions and the browser can re-run it at animation rate between polls
 * without any hand-off state. Anything this emits is tier "scenario" — the
 * adapter and the UI must never present it as live GPS.
 *
 * Model
 *   - Service: Mon–Fri 07:00–19:00 all lines; Sat 07:00–19:00 lines 1–2 only;
 *     Sun none (chula.ac.th/en/about/green-university/cu-shuttle-bus).
 *   - Cruise 20 km/h, 60 s dwell at every stop, 4 buses per line evenly
 *     spaced by cycle time / 4.
 *   - Each bus runs the closed loop in cu-shuttle-lines.geojson forever
 *     within service hours; position = distance along the line, interpolated
 *     on the road polyline, bearing from the local segment.
 */

import type { Feature, FeatureCollection, LineString, Position } from "geojson";
import { seedNoise } from "./seedNoise";

export const CRUISE_KMH = 20;
export const CRUISE_MPS = CRUISE_KMH / 3.6;
export const DWELL_S = 60;
export const BUSES_PER_LINE = 4;
export const SERVICE_TZ = "Asia/Bangkok";
export const SERVICE_START_MIN = 7 * 60;  // 07:00
export const SERVICE_END_MIN = 19 * 60;   // 19:00

export type Weekday = "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat";
const WEEKDAYS: Weekday[] = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export interface ShuttleStopMark {
  id: string;
  name: string;
  /** Cumulative metres from the line's start vertex. */
  distM: number;
}

export interface ShuttleLineProps {
  line: string;
  color: string;
  days: Weekday[];
  lengthM: number;
  stops: ShuttleStopMark[];
}

export type ShuttleLineFeature = Feature<LineString, ShuttleLineProps>;
export type ShuttleLines = FeatureCollection<LineString, ShuttleLineProps>;

/** Matches apps/api/src/adapters/cuShuttle.ts `ShuttleVehicle` — the live-feed contract. */
export interface SimVehicle {
  id: string;
  line: string;
  lat: number;
  lng: number;
  bearing: number;
  speedKmh: number;
  occupancy: "low" | "medium" | "high";
  /** Stop the bus is currently dwelling at, if any. */
  atStop?: string;
  /** Next stop on the loop. */
  nextStop: string;
  updatedAt: string;
}

// ── Clock ───────────────────────────────────────────────────────────────────

/** Bangkok-local weekday + minutes-since-midnight for an instant. */
export function bangkokClock(now: Date): { weekday: Weekday; minutes: number; seconds: number } {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: SERVICE_TZ, weekday: "short", hour: "numeric", minute: "numeric", second: "numeric", hour12: false,
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "0";
  const weekday = get("weekday").toLowerCase().slice(0, 3) as Weekday;
  // hour12:false can yield "24" at midnight in some engines.
  const hour = Number(get("hour")) % 24;
  const minute = Number(get("minute"));
  const second = Number(get("second"));
  return { weekday, minutes: hour * 60 + minute, seconds: hour * 3600 + minute * 60 + second };
}

export function isInService(line: ShuttleLineProps, clock: { weekday: Weekday; minutes: number }): boolean {
  if (!line.days.includes(clock.weekday)) return false;
  return clock.minutes >= SERVICE_START_MIN && clock.minutes < SERVICE_END_MIN;
}

// ── Geometry along the line ─────────────────────────────────────────────────

const R_EARTH = 6371000;
export function metres(a: Position, b: Position): number {
  const dLng = ((b[0] - a[0]) * Math.PI) / 180;
  const dLat = ((b[1] - a[1]) * Math.PI) / 180;
  const x = dLng * Math.cos((((a[1] + b[1]) / 2) * Math.PI) / 180);
  return R_EARTH * Math.hypot(x, dLat);
}

/** Compass bearing 0–360 from a to b (0 = north, 90 = east). */
export function bearing(a: Position, b: Position): number {
  const φ1 = (a[1] * Math.PI) / 180, φ2 = (b[1] * Math.PI) / 180;
  const Δλ = ((b[0] - a[0]) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Cumulative vertex distances, computed once per line and memoised by reference. */
const cumCache = new WeakMap<ShuttleLineFeature, number[]>();
function cumulative(line: ShuttleLineFeature): number[] {
  const hit = cumCache.get(line);
  if (hit) return hit;
  const c = line.geometry.coordinates;
  const out = [0];
  for (let i = 1; i < c.length; i++) out.push(out[i - 1] + metres(c[i - 1], c[i]));
  cumCache.set(line, out);
  return out;
}

/** Point + heading at `distM` along the line (wraps around the loop). */
export function pointAlong(line: ShuttleLineFeature, distM: number): { lng: number; lat: number; bearing: number } {
  const c = line.geometry.coordinates;
  const cum = cumulative(line);
  const total = cum[cum.length - 1];
  const d = total > 0 ? ((distM % total) + total) % total : 0;
  // Binary search for the segment containing d.
  let lo = 0, hi = cum.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (cum[mid] <= d) lo = mid; else hi = mid;
  }
  const a = c[lo], b = c[Math.min(lo + 1, c.length - 1)];
  const segLen = cum[Math.min(lo + 1, cum.length - 1)] - cum[lo];
  const t = segLen > 0 ? (d - cum[lo]) / segLen : 0;
  return {
    lng: a[0] + (b[0] - a[0]) * t,
    lat: a[1] + (b[1] - a[1]) * t,
    bearing: bearing(a, b),
  };
}

// ── Timeline: where is a bus `t` seconds into its cycle? ────────────────────

/** One loop = drive every stop→stop leg at cruise speed, dwelling DWELL_S at each stop. */
export function cycleSeconds(line: ShuttleLineProps): number {
  return line.lengthM / CRUISE_MPS + line.stops.length * DWELL_S;
}

interface Placement {
  distM: number;
  moving: boolean;
  atStop?: ShuttleStopMark;
  nextStop: ShuttleStopMark;
}

/**
 * Walk the loop timeline. Stops are sorted by distM; each stop contributes a
 * dwell window at its position, then a drive window to the next stop.
 */
export function placeAtCycleTime(line: ShuttleLineProps, tCycle: number): Placement {
  const stops = line.stops;
  const cycle = cycleSeconds(line);
  let t = ((tCycle % cycle) + cycle) % cycle;
  for (let i = 0; i < stops.length; i++) {
    const stop = stops[i];
    const next = stops[(i + 1) % stops.length];
    // Dwell at this stop.
    if (t < DWELL_S) return { distM: stop.distM, moving: false, atStop: stop, nextStop: next };
    t -= DWELL_S;
    // Drive to the next stop (last leg wraps to lengthM then 0).
    const legM = (i + 1 < stops.length ? next.distM : line.lengthM) - stop.distM;
    const legS = legM / CRUISE_MPS;
    if (t < legS) return { distM: stop.distM + CRUISE_MPS * t, moving: true, nextStop: next };
    t -= legS;
  }
  // Floating-point tail: land on the first stop.
  return { distM: stops[0].distM, moving: false, atStop: stops[0], nextStop: stops[1 % stops.length] };
}

// ── Fleet ───────────────────────────────────────────────────────────────────

function occupancyFor(id: string, minutes: number): SimVehicle["occupancy"] {
  // Two humps (08:00 arrivals, 16:30 departures) over a quiet midday, jittered per bus.
  const h = minutes / 60;
  const load = Math.exp(-((h - 8.2) ** 2) / 1.6) + Math.exp(-((h - 16.5) ** 2) / 2.0);
  const v = load * 0.8 + seedNoise(id, Math.floor(minutes / 10)) * 0.4;
  return v > 0.9 ? "high" : v > 0.45 ? "medium" : "low";
}

/**
 * Every in-service bus for the instant `now`. Empty outside service hours.
 * Deterministic: identical `now` → identical output, byte for byte.
 */
export function simulateFleet(lines: ShuttleLines, now: Date): SimVehicle[] {
  const clock = bangkokClock(now);
  const sinceStart = clock.seconds - SERVICE_START_MIN * 60; // seconds since 07:00
  const updatedAt = now.toISOString();
  const out: SimVehicle[] = [];

  for (const feature of lines.features) {
    const line = feature.properties;
    if (!isInService(line, clock) || line.stops.length < 2 || line.lengthM <= 0) continue;
    const cycle = cycleSeconds(line);
    for (let k = 0; k < BUSES_PER_LINE; k++) {
      // Bus k departs stop 0 at k × headway after 07:00; before that it isn't out yet.
      const offset = (k * cycle) / BUSES_PER_LINE;
      const tCycle = sinceStart - offset;
      if (tCycle < 0) continue;
      const id = `POP-${line.line}0${k + 1}`; // POP-101 … POP-504 — matches chula-vehicles' fleet ids
      const p = placeAtCycleTime(line, tCycle);
      const pt = pointAlong(feature, p.distM);
      out.push({
        id,
        line: line.line,
        lat: Number(pt.lat.toFixed(6)),
        lng: Number(pt.lng.toFixed(6)),
        bearing: Math.round(pt.bearing),
        speedKmh: p.moving ? CRUISE_KMH : 0,
        occupancy: occupancyFor(id, clock.minutes),
        atStop: p.atStop?.name,
        nextStop: p.nextStop.name,
        updatedAt,
      });
    }
  }
  return out;
}
